<template>
  <div>
    <ValidationObserver ref="formAddEvaluation">
      <form novalidate @submit.prevent>
        <div class="row pl-3">
          <div class="col-3 py-0">
            <ValidationProvider
              v-slot="{ errors }"
              name="Fecha"
              :rules="activeRules.date"
            >
              <CustomDatePicker
                v-model="model.date"
                :disabled="isEdit && !canEditDate && model.canEdit"
                label="Fecha:"
                :max-date="new Date()"
                :custom-error-msg="'Favor seleccionar fecha.'"
                :error="errors[0]"
              />
            </ValidationProvider>
          </div>

          <div class="col-5 py-0">
            <ValidationProvider
              v-slot="{ errors }"
              name="Disciplina"
              :rules="activeRules.discipline"
            >
              <CustomDropDown
                v-model="model.discipline"
                :options="catalog.disciplines"
                :disabled="isEdit"
                fieldkey="id"
                fieldname="name"
                label="Disciplina:"
                placeholder="Seleccione"
                :error="errors[0]"
                :custom-error-msg="messages.required"
              />
            </ValidationProvider>
          </div>

          <div class="col-4 py-0">
            <ValidationProvider
              v-slot="{ errors }"
              name="Administrado por"
              :rules="activeRules.administrator"
            >
              <CustomInput
                v-model="model.administrator"
                :disabled="isEdit"
                label="Administrado por:"
                :error="errors[0]"
                :custom-error-msg="messages.required"
              />
            </ValidationProvider>
          </div>
          <div
            v-if="canEditParentAproval && model.canEdit && canEditParentAproval"
            class="col-12 mt-4 px-2"
          >
            <CustomCheckboxSingleOption
              v-model="model.isApprovedByParentFlag"
              option-text="Esta evaluacion ha sido aprobada por los padres."
            />
          </div>
          <div class="col-12 mt-4 px-2">
            <ValidationProvider
              v-slot="{ errors }"
              name="Comentario"
              :rules="activeRules.comment"
            >
              <CustomTextArea
                v-model="model.comment"
                :disabled="isEdit"
                label="Comentario:"
                :maxlength="maxLength"
                :show-char-count="true"
                :error="errors[0]"
                :custom-error-msg="messages.required"
              />
            </ValidationProvider>
          </div>
        </div>

        <div class="row pl-2 pt-2 mb-2">
          <CustomRadio
            v-model="model.reevaluationRequired"
            name="reevaluationRequired"
            class="mt-4 mb-2"
            :disabled="isCheckDisabled"
            label="Requiere reevaluación a partir de la implementación de las recomendaciones ofrecidas en este informe de evaluación."
            :options="radioButtonsOptions"
            is-inline
          />
        </div>

        <template v-if="model.reevaluationRequired">
          <div class="row pl-4 mt-2">
            <div class="col-12 bg-secondary-3 py-2">
              <div class="row py-2">
                <div class="col-6 col-md-6">
                  <ValidationProvider
                    v-slot="{ errors }"
                    mode="eager"
                    :rules="showReevaluationRequiredFields ? 'required' : ''"
                  >
                    <CustomDropDown
                      v-model="model.concatenatedEvaluationTime"
                      :options="concatenatedReevaluationTime"
                      fieldkey="id"
                      :disabled="isEdit"
                      fieldname="name"
                      label="Tiempo para reevaluación:"
                      placeholder="Seleccione"
                      :error="errors[0]"
                      :custom-error-msg="messages.reevaluationTime"
                      @input="concatenatedEvaluationChange"
                    />
                  </ValidationProvider>
                </div>
              </div>

              <div class="row py-2">
                <div class="col-12 col-md-12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :rules="showReevaluationRequiredFields ? 'required' : ''"
                  >
                    <CustomTextArea
                      v-model="model.reevaluationPurpose"
                      :disabled="isEdit"
                      label="¿Con qué finalidad?"
                      :maxlength="3000"
                      :show-char-count="true"
                      :error="errors[0]"
                      custom-error-msg="Favor explique con que finalidad."
                    />
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </div>
        </template>
      </form>
    </ValidationObserver>

    <lazy-documents
      :documents="model.documents"
      :prevent-duplicated-types="[1, 2]"
      :emit-download="isEdit"
      :soft-delete="isEdit"
      @document-added="messages.documents.show = false"
      @download-document="downloadDocument"
    />

    <error
      v-if="messages.documents.show"
      :message="messages.documents.text"
      variant="no-border"
    />
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  CustomDropDown,
  CustomTextArea,
  CustomDatePicker,
  CustomInput,
  CustomCheckboxSingleOption,
} from '@/components/common';
import Error from '@/components/common/Error';
import LazyDocuments from '@/components/common/documents/LazyDocuments';
import alert from '@/mixin/alert.mixin';
import codeService from '@/modules/common/code.api';
import CustomCheckbox from 'CheckBox';
import CustomRadio from 'custom-radio';

import categories from '@/utils/constants/categoriesFlag';
import serviceTypes from '@/utils/constants/service-types';
import DISCIPLINE_TYPES from '@/utils/constants/disciplineTypes';

export default {
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    serviceTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    validFor: {
      type: Number,
      default: 0,
      required: false,
    },
    maxLength: {
      type: Number,
      default: 500,
      required: false,
    },
    model: {
      type: Object,
      default: () => ({
        date: new Date(),
        discipline: null,
        administrator: '',
        comment: '',
        documents: [],
        isApprovedByParentFlag: new Boolean(),
        reevaluationRequired: false,
        reevaluationTime: '',
        reevaluationPeriod: '',
        concatenatedEvaluationTime: null,
      }),
      required: true,
    },
    canEditDate: {
      type: Boolean,
      default: false,
    },
    canEditParentAproval: {
      type: Boolean,
      default: null,
    },
  },
  data: () => ({
    concatenatedReevaluationTime: [],
    radioButtonsOptions: [
      { label: 'Si', value: true },
      { label: 'No', value: false },
    ],
    messages: {
      required: 'Campo requerido.',
      documents: {
        text: 'Documento requerido.',
        show: false,
      },
      reevaluationPurpose: 'Favor explique con que finalidad.',
      reevaluationTime: 'El tiempo para reevaluación es requerido.',
      reevaluationYearMonth: 'El periodo de año y mes es requerido.',
    },

    catalog: {
      disciplines: [],
    },
    reevaluationPeriodOptions: [],
    isCheckDisabled: false,
  }),
  watch: {
    'model.discipline'(value) {
      if (value.id == DISCIPLINE_TYPES.AsistenciaTecnologia) {
        this.isCheckDisabled = true;
        this.model.reevaluationRequired = false;
      } else {
        this.isCheckDisabled = false;
      }
    },
  },
  computed: {
    rules() {
      return {
        date: { required: true },
        discipline: { required: true },
        administrator: { required: true },
        comment: { required: true, max: this.maxLength },
      };
    },
    activeRules() {
      return this.isEdit ? {} : this.rules;
    },
    validDocuments() {
      return this.model.documents.filter((x) => !x.deleted);
    },
    showReevaluationRequiredFields() {
      return this.model.reevaluationRequired;
    },
    discussionMode() {
      return !!this.$route.query.discussionMode;
    },
  },
  components: {
    ValidationProvider,
    ValidationObserver,
    CustomDropDown,
    CustomTextArea,
    CustomDatePicker,
    CustomInput,
    Error,
    LazyDocuments,
    CustomCheckboxSingleOption,
    CustomCheckbox,
    CustomRadio,
  },
  async mounted() {
    this.catalog.disciplines = await this.fetchDisciplines();
    await this.fetchReevaluationPeriods();
    this.getReevaluationTimes();
  },
  methods: {
    concatenatedEvaluationChange() {
      this.model.reevaluationTime = this.model.concatenatedEvaluationTime.unitOfTime;
      this.model.reevaluationPeriod = this.model.concatenatedEvaluationTime.reevaluationPeriodId;
    },
    getReevaluationTimes() {
      this.$ApiGet('/api/code/reevaluationtimes')
        .then((res) => {
          if (this.isEdit) {
            this.concatenatedReevaluationTime = res.data;
          } else {
            this.concatenatedReevaluationTime = res.data.filter(
              (x) => !x.isDisabledTime
            );
          }
        })
        .catch((err) => {
          this.response = err;
        });
    },
    async fetchDisciplines() {
      const options = this.discussionMode
        ? {
            serviceTypeIds: [serviceTypes.EvaluacionAdicional],
            categories: [categories.unSet],
          }
        : {
            serviceTypeIds: [this.serviceTypes],
            categories: [this.validFor],
            disciplineIds: [DISCIPLINE_TYPES.AsistenciaTecnologia],
          };

      const data = await codeService.getDisciplineTypes(options);

      return data;
    },
    async validation() {
      const formValid = await this.$refs.formAddEvaluation.validate();
      const documentsValid = this.validDocuments.length > 0;

      this.messages.documents.show = !documentsValid;

      const allValid = [formValid, documentsValid].every((x) => !!x);
      return allValid;
    },
    downloadDocument({ file }) {
      try {
        const byteChars = atob(file.content);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: file.contentType });
        const link = window.document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = file.name;
        link.click();
      } catch {
        this.ShowToast(
          'Ha ocurrido un error al descargar la descarga.',
          'Error',
          'error'
        );
      }
    },
    async fetchReevaluationPeriods() {
      const { data } = await codeService.getStudentReevaluationPeriods();

      data.map((item) => {
        const option = {
          label: item.name,
          value: item.id,
        };

        this.reevaluationPeriodOptions.push(option);
      });
    },
  },
  mixins: [alert],
};
</script>

<style></style>
